/**
 * Round composition (SPEC §6).
 *
 * The builder decides what the learner sees next, in what mode, and whether a scaffold is offered.
 * It is the enforcement point for four invariants at once, so its ordering is not incidental:
 *
 *   1. **Reviews are never cut** (invariants 6, 7). Due reviews go into the pool first and nothing
 *      may displace them — not intake, not the grammar cap, not escalation.
 *   2. **Interleave tiers and modes** (invariant 8). Never block by topic.
 *   3. **The 25% grammar cap** (invariant 9), enforced per draw, with due reviews exempt.
 *   4. **Mystery** (RESEARCH §3). The next `(item, mode)` is selected from the seeded `Rng`; the
 *      learner is shown nothing about what is coming.
 *
 * Pure: no clock, no `Math.random()`, no I/O. Time and randomness are arguments.
 *
 * T27a: pool construction.
 * T27b: tier/mode interleaving.
 * T27c: the 25% cap with review exemption and displacement logging.
 * T27d: escalation.
 */

import type { Rng } from '../core/rng';
import type { ModeId, Tier } from '../state/gamestate';
import type { ItemRecord } from '../store/schema';
import type { UnlockMap } from '../scheduler/gating';
import {
  planIntake,
  type IntakeConfig,
  type IntakePlan,
} from '../scheduler/intake';
import {
  dueQueue,
  estimatedSeconds,
  playableModes,
  playableQueue,
  type SecondsPerItem,
} from '../scheduler/queue';
import {
  DEFAULT_QUOTA,
  GRAMMAR_CAP,
  GrammarLedger,
  isGrammarTier,
  type DrawRecord,
  type QuotaConfig,
} from './quota';

export interface Capabilities {
  speechAvailable: boolean;
}

/** One item in the composition pool, with the fact the grammar cap turns on. */
export interface PoolEntry {
  item: ItemRecord;
  /** Due review (exempt from cap displacement) or discretionary intake. */
  wasDueReview: boolean;
}

export interface PoolInput {
  items: readonly ItemRecord[];
  now: number;
  unlocked: UnlockMap;
  capabilities: Capabilities;
  /** Epoch ms of every previous new-item introduction. See `intake.introductionTimes`. */
  introducedAt: readonly number[];
  measured?: SecondsPerItem;
  intakeConfig?: IntakeConfig;
}

export interface Pool {
  /** Due reviews, in risk order, complete. */
  reviews: PoolEntry[];
  /** New items admitted this run, in content order. */
  newItems: PoolEntry[];
  /** Reviews followed by new items. The interleaver's input. */
  entries: PoolEntry[];
  intake: IntakePlan;
}

/**
 * New-item candidates: items never reviewed, at an unlocked tier, answerable on this device.
 *
 * Ordered by tier and then by id — for the seed corpus, authoring order, function words first
 * (SPEC §5, tier 2) — and then **re-mixed so that grammar candidates arrive at the rate the cap
 * can actually accept**.
 *
 * The re-mix is not cosmetic. Tier-ascending order alone offers the whole of Motor and Conversión
 * before a single tier-5 phrase, so once the tier-2 supply is spent a day's ten intake slots are
 * all grammar, the cap correctly refuses seven of them, and the learner gets three new words
 * instead of ten — the cap starving intake rather than shaping it. Found by the T28 simulation at
 * round 58. See DECISIONS D-060.
 *
 * Content priority is preserved *within* each class; only the interleaving between the two
 * classes changes. The builder's interleaver randomises where a new item lands in the run; neither
 * step randomises which new words are introduced.
 */
export function intakeCandidates(
  items: readonly ItemRecord[],
  unlocked: UnlockMap,
  capabilities: Capabilities,
  cap: number = GRAMMAR_CAP,
): ItemRecord[] {
  const ordered = items
    .filter((item) => item.memory.state === 'new')
    .filter((item) => unlocked[item.tier])
    .filter((item) => playableModes(item, capabilities).length > 0)
    .sort((a, b) => (a.tier !== b.tier ? a.tier - b.tier : a.id < b.id ? -1 : a.id > b.id ? 1 : 0));

  /*
   * Two passes, in this order.
   *
   * The grammar mix runs over everything that is not a sound, so the 25% grammar cap is computed
   * against the vocabulary it is competing with. The sounds are then folded into that result at
   * their own rate — folding them in first would have them diluting the grammar share and quietly
   * letting more grammar through than invariant 9 allows.
   */
  const sounds = ordered.filter((item) => item.tier === SOUND_TIER);
  const rest = ordered.filter((item) => item.tier !== SOUND_TIER);
  return mixAtRate(sounds, mixToCap(rest, cap), SOUND_SHARE);
}

/** Tier 1. Named here so the two places that special-case it cannot drift apart. */
const SOUND_TIER = 1;

/**
 * Merge grammar and non-grammar candidates so grammar arrives at no more than `cap` of the list.
 *
 * Whatever is left of either class is appended once the other runs out — this shapes the offer, it
 * never withholds content. If the corpus has nothing but grammar left to teach, the list is all
 * grammar and the per-draw cap does the rest.
 */
function mixToCap(ordered: readonly ItemRecord[], cap: number): ItemRecord[] {
  return mixAtRate(
    ordered.filter((item) => isGrammarTier(item.tier)),
    ordered.filter((item) => !isGrammarTier(item.tier)),
    cap,
  );
}

/**
 * How much of the intake offer tier 1 may take (T62).
 *
 * Sonido is a small fixed set — thirty-odd sounds — and intake orders by tier, so without this the
 * whole of it is offered before a single word is: with a microphone present, day one became ten
 * pronunciation drills and no vocabulary, and day four was the first day the learner met a noun.
 * The ladder is about what *unlocks* what (invariant 4), never about finishing a tier before
 * starting the next. One card in four is enough for the sounds to be constantly present.
 *
 * The same shape of problem as the grammar cap and, deliberately, the same mechanism — but a
 * different reason, so a different number and a different name. See DECISIONS D-197.
 */
export const SOUND_SHARE = 0.25;

/**
 * Merge two ordered lists so `bounded` never exceeds `cap` of what has been placed.
 *
 * Whatever is left of either class is appended once the other runs out — this shapes the offer, it
 * never withholds content. Extracted from `mixToCap` when tier 1 turned out to need exactly the
 * same treatment for a completely different reason.
 */
function mixAtRate(
  bounded: readonly ItemRecord[],
  rest: readonly ItemRecord[],
  cap: number,
): ItemRecord[] {
  if (bounded.length === 0) return [...rest];
  if (rest.length === 0) return [...bounded];

  const out: ItemRecord[] = [];
  let b = 0;
  let r = 0;
  // Running count rather than a filter per iteration: this is O(n) on a list that is the whole
  // unseen corpus, and it is rebuilt on every round.
  let placed = 0;
  while (b < bounded.length || r < rest.length) {
    const projected = (placed + 1) / (out.length + 1);
    const takeBounded = b < bounded.length && (r >= rest.length || projected <= cap);
    if (takeBounded) {
      out.push(bounded[b]!);
      placed += 1;
      b += 1;
    } else {
      out.push(rest[r]!);
      r += 1;
    }
  }
  return out;
}

/**
 * Build the composition pool: every due review, then whatever intake allows.
 *
 * The two lists stay separate all the way through the builder. Merging them into one array of
 * items and losing track of which was which is how a cap or a truncation ends up eating a review
 * by accident.
 */
export function buildPool(input: PoolInput): Pool {
  const due = playableQueue(dueQueue(input.items, input.now), input.capabilities);
  const candidates = intakeCandidates(input.items, input.unlocked, input.capabilities);

  const intake = planIntake({
    dueQueue: due,
    candidates,
    introducedAt: input.introducedAt,
    now: input.now,
    ...(input.measured ? { measured: input.measured } : {}),
    ...(input.intakeConfig ? { config: input.intakeConfig } : {}),
  });

  const reviews: PoolEntry[] = intake.reviews.map((item) => ({ item, wasDueReview: true }));
  const newItems: PoolEntry[] = intake.newItems.map((item) => ({ item, wasDueReview: false }));

  return { reviews, newItems, entries: [...reviews, ...newItems], intake };
}

/** Modes an entry can actually be drawn in, on this device. */
export function modesFor(entry: PoolEntry, capabilities: Capabilities): ModeId[] {
  return playableModes(entry.item, capabilities);
}

export function tierOf(entry: PoolEntry): Tier {
  return entry.item.tier;
}

/**
 * Pick a mode for an entry from the seeded stream.
 *
 * Mystery is a mechanic here, not decoration (RESEARCH §3): the learner must not be able to
 * predict the mode from the item. Throws on an item with no playable mode, because a card with
 * nothing to render is always a content bug and never something to paper over.
 */
export function pickMode(entry: PoolEntry, capabilities: Capabilities, rng: Rng): ModeId {
  const modes = modesFor(entry, capabilities);
  if (modes.length === 0) {
    throw new RangeError(`item ${entry.item.id} has no playable mode on this device`);
  }
  return rng.pick(modes);
}

// ---------------------------------------------------------------------------------------------
// T27b — interleaving
// ---------------------------------------------------------------------------------------------

/**
 * How far down the priority-ordered pool the interleaver may reach for the next draw.
 *
 * This is the knob that trades risk-ordering against mystery. At 1 the sequence is fully
 * predictable from the queue; at pool length it is pure noise and the most fragile item might not
 * come up before the timer expires. Three keeps the highest-risk reviews near the front of the run
 * — which is what matters in a six-minute 3:40 a.m. session — while making the next card genuinely
 * unguessable (RESEARCH §3, Mystery).
 */
export const DEFAULT_LOOKAHEAD = 3;

export interface InterleaveOptions {
  capabilities: Capabilities;
  rng: Rng;
  lookahead?: number;
  /**
   * Late-run tier skew (T27d, SPEC §6.1 principle 3). When supplied, draws in the final third of
   * the expected run bias toward the highest tier still available.
   *
   * This lives in the interleaver rather than in a post-cap escalation pass, even though SPEC
   * §6.2's pseudocode runs escalation last: reordering draws *after* the cap has been applied
   * would invalidate the per-prefix guarantee DoD-2 asserts. See DECISIONS D-055.
   */
  escalation?: { expectedDraws: number; skewProbability?: number };
}

export interface DraftDraw {
  entry: PoolEntry;
  mode: ModeId;
}

/**
 * Order the pool so that tiers and modes interleave (invariant 8: never block by topic).
 *
 * Two constraints, both from SPEC §6.1:
 *   - no two consecutive draws share a tier **where the pool allows an alternative**;
 *   - no three consecutive draws share a mode.
 *
 * Both are conditional on an alternative existing. When the whole remaining pool is one tier —
 * eleven due tier-2 reviews and nothing else — the constraint yields rather than dropping items.
 * Interleaving is a presentation rule; it never outranks "every due review is emitted".
 */
export function interleave(
  entries: readonly PoolEntry[],
  options: InterleaveOptions,
): DraftDraw[] {
  const lookahead = options.lookahead ?? DEFAULT_LOOKAHEAD;
  const remaining = [...entries];
  const out: DraftDraw[] = [];

  let lastTier: Tier | null = null;
  let lastMode: ModeId | null = null;
  let secondLastMode: ModeId | null = null;

  while (remaining.length > 0) {
    const eligible = remaining.filter((entry) => tierOf(entry) !== lastTier);
    // Empty means every remaining item shares the previous tier. Emit anyway.
    const source = eligible.length > 0 ? eligible : remaining;
    const window = source.slice(0, Math.max(1, lookahead));
    // Skew first, then drain evenly *within* what the skew left. The other order lets the
    // abundance preference cancel the skew out: once the top tier has been drawn a few times it
    // is no longer the most abundant, and escalation would quietly stop happening.
    const skewed = applyLateRunSkew(window, out.length, options);
    const entry = options.rng.pick(preferAbundantTier(skewed, remaining));
    remaining.splice(remaining.indexOf(entry), 1);

    const mode = pickSpacedMode(entry, options.capabilities, options.rng, lastMode, secondLastMode);
    out.push({ entry, mode });

    lastTier = tierOf(entry);
    secondLastMode = lastMode;
    lastMode = mode;
  }

  return out;
}

/**
 * Within the lookahead window, prefer entries from whichever tier has the most left.
 *
 * Without this the pool drains unevenly and the last handful of draws are all one tier, producing
 * a same-tier run that the constraint would have allowed earlier. Draining the abundant tiers
 * first keeps alternatives available for as long as the pool possibly can, so the forced run at
 * the tail is as short as the composition permits.
 *
 * The choice among equally-abundant candidates stays random, so this narrows the shuffle without
 * making the next card predictable.
 */
function preferAbundantTier(
  window: readonly PoolEntry[],
  remaining: readonly PoolEntry[],
): PoolEntry[] {
  const counts = new Map<Tier, number>();
  for (const entry of remaining) {
    counts.set(tierOf(entry), (counts.get(tierOf(entry)) ?? 0) + 1);
  }
  let best = 0;
  for (const entry of window) best = Math.max(best, counts.get(tierOf(entry)) ?? 0);
  return window.filter((entry) => (counts.get(tierOf(entry)) ?? 0) === best);
}

/** Default strength of the late-run skew. Not 1: a fully deterministic final third is guessable. */
export const DEFAULT_SKEW_PROBABILITY = 0.7;

/**
 * In the final third of the expected run, bias toward the highest tier still on offer.
 *
 * Escalating difficulty is one of the three gamification components that carry a real effect
 * (RESEARCH §3, Challenge); a flat run is the version the evidence says does nothing. The skew is
 * probabilistic rather than absolute because a final third that always serves the top tier is
 * predictable, and predictability is the thing mystery exists to protect.
 */
function applyLateRunSkew(
  candidates: PoolEntry[],
  position: number,
  options: InterleaveOptions,
): PoolEntry[] {
  const escalation = options.escalation;
  if (!escalation || escalation.expectedDraws <= 0) return candidates;
  if (position < (escalation.expectedDraws * 2) / 3) return candidates;
  if (options.rng.next() >= (escalation.skewProbability ?? DEFAULT_SKEW_PROBABILITY)) {
    return candidates;
  }
  const highest = Math.max(...candidates.map(tierOf));
  return candidates.filter((entry) => tierOf(entry) === highest);
}

/**
 * A mode for this entry that does not make three of the same in a row.
 *
 * Falls back to the unconstrained choice when the item has no alternative — a tier-3 item is only
 * ever `conjugate`, and refusing to draw it would be the mode constraint quietly cutting a review.
 */
function pickSpacedMode(
  entry: PoolEntry,
  capabilities: Capabilities,
  rng: Rng,
  lastMode: ModeId | null,
  secondLastMode: ModeId | null,
): ModeId {
  const modes = modesFor(entry, capabilities);
  if (modes.length === 0) {
    throw new RangeError(`item ${entry.item.id} has no playable mode on this device`);
  }
  const wouldBeThird = lastMode !== null && lastMode === secondLastMode ? lastMode : null;
  const allowed = wouldBeThird === null ? modes : modes.filter((m) => m !== wouldBeThird);
  return rng.pick(allowed.length > 0 ? allowed : modes);
}

/**
 * Longest run of consecutive draws sharing a tier. Exported because it is the thing the
 * interleaving tests assert on, and a shared definition beats each test re-deriving it.
 */
export function longestTierRun(draws: readonly DraftDraw[]): number {
  return longestRun(draws.map((d) => String(tierOf(d.entry))));
}

/** Longest run of consecutive draws sharing a mode. */
export function longestModeRun(draws: readonly DraftDraw[]): number {
  return longestRun(draws.map((d) => d.mode));
}

function longestRun(values: readonly string[]): number {
  let best = 0;
  let current = 0;
  let previous: string | null = null;
  for (const value of values) {
    current = value === previous ? current + 1 : 1;
    previous = value;
    if (current > best) best = current;
  }
  return best;
}

/**
 * The tier-spacing constraint exactly as SPEC §6.1 states it: no two consecutive draws share a
 * tier **where the pool allows an alternative**.
 *
 * Returns the indices where two consecutive draws share a tier *and* a different tier was still
 * available at that point. An empty result is compliance; `longestTierRun` is the cruder measure
 * and will legitimately exceed 1 at the tail of a pool that has run out of alternatives.
 */
export function tierSpacingViolations(draws: readonly DraftDraw[]): number[] {
  const out: number[] = [];
  for (let i = 1; i < draws.length; i++) {
    const tier = tierOf(draws[i]!.entry);
    if (tier !== tierOf(draws[i - 1]!.entry)) continue;
    // An alternative existed if anything still unemitted at this point was a different tier.
    const alternativeExisted = draws.slice(i).some((d) => tierOf(d.entry) !== tier);
    if (alternativeExisted) out.push(i);
  }
  return out;
}

/**
 * The mode-spacing constraint as SPEC §6.1 states it: no three consecutive draws share a mode
 * where the items involved had an alternative mode to offer.
 */
export function modeSpacingViolations(
  draws: readonly DraftDraw[],
  capabilities: Capabilities,
): number[] {
  const out: number[] = [];
  for (let i = 2; i < draws.length; i++) {
    const mode = draws[i]!.mode;
    if (draws[i - 1]!.mode !== mode || draws[i - 2]!.mode !== mode) continue;
    // Tier 3 is only ever `conjugate`; refusing to draw it would be the mode constraint quietly
    // cutting a review, so an item with no alternative mode is not a violation.
    if (modesFor(draws[i]!.entry, capabilities).length > 1) out.push(i);
  }
  return out;
}

// ---------------------------------------------------------------------------------------------
// T27c — the 25% grammar cap, enforced per draw
// ---------------------------------------------------------------------------------------------

/**
 * A draw the run can actually present.
 *
 * `at` is a *projected* timestamp, one millisecond per position, used only for quota accounting
 * inside a seven-day window. The real attempt time comes from the injected clock when the item is
 * banked; nothing downstream reads this as a measurement.
 */
export interface Draw {
  index: number;
  item: ItemRecord;
  tier: Tier;
  mode: ModeId;
  wasDueReview: boolean;
  at: number;
}

export interface Displacement {
  itemId: string;
  tier: Tier;
  mode: ModeId;
  /** The only reason a draw is ever displaced. There is no other displacement path. */
  reason: 'grammar-cap';
  /** Position in the interleaved sequence where the cap refused it. */
  atIndex: number;
  /** Re-offered later in this same run once the window allowed it. */
  reoffered: boolean;
}

export interface CapResult {
  draws: Draw[];
  displaced: Displacement[];
  /** Displaced draws the window never made room for. Re-offered next run; not lost. */
  deferred: DraftDraw[];
  ledger: GrammarLedger;
  /**
   * Positions where a spacing constraint had to give way, split by *what* forced it.
   *
   * The split is the whole point. An earlier single counter conflated the two and was used to
   * justify the interleaving assertion in T28; measured, it fired on 72% of draws, almost all of
   * them `poolForced`, which made that assertion nearly vacuous. See DECISIONS D-063.
   */
  tierCompromises: SpacingCompromises;
  modeCompromises: SpacingCompromises;
}

/**
 * Why a spacing constraint gave way at a given position.
 *
 * Interleaving is conditional on the pool allowing an alternative (SPEC §6.1), so only one of
 * these is a genuine trade-off the design makes:
 *
 *  - `poolForced` — no draw of another tier (or another mode) was left at all. The constraint is
 *    not applicable; there is nothing to interleave with. Expected, and uninteresting.
 *  - `capForced` — such a draw existed but the 25% cap refused it. This is the real trade-off:
 *    invariant 9 overriding the interleaving heuristic. Worth watching, and worth failing on if it
 *    ever becomes routine.
 */
export interface SpacingCompromises {
  capForced: number;
  poolForced: number;
}

export function totalCompromises(c: SpacingCompromises): number {
  return c.capForced + c.poolForced;
}

export interface CapOptions {
  now: number;
  /** The persisted seven-day draw window, so a new run does not start from an empty ledger. */
  history: readonly DrawRecord[];
  quotaConfig?: QuotaConfig;
  /**
   * How much clock time the run will consume. Passed to the ledger so the cap holds against every
   * window a measurement during the run could use, not only the one at the run's first instant
   * (DECISIONS D-061).
   */
  runMs?: number;
}

/**
 * Enforce the 25% cap per draw (SPEC §6.3, as amended by DECISIONS D-018).
 *
 * The rule in one line: **a due review is always emitted; a discretionary grammar draw is emitted
 * only if the discretionary share would still be at or under the cap afterwards.**
 *
 * What the cap does to a refused draw matters as much as the refusal. It is not dropped: it goes
 * to a deferred list, is retried at every subsequent position, and is emitted the moment the
 * window allows — usually a few draws later, once enough non-grammar draws have landed. What is
 * still deferred at the end of the sequence is re-offered next run. Nothing is lost; the cap
 * governs *when*, never *whether*.
 *
 * The cap therefore cannot end a run early. Displacing a grammar draw does not stop the sequence;
 * the following draw is presented instead, and the displaced one rejoins later.
 */
export function applyGrammarCap(drafts: readonly DraftDraw[], options: CapOptions): CapResult {
  const ledger = new GrammarLedger(
    options.history,
    options.now,
    options.quotaConfig ?? DEFAULT_QUOTA,
    options.runMs ?? DEFAULT_RUN_MS,
  );
  const draws: Draw[] = [];
  const displaced: Displacement[] = [];
  const displacementOf = new Map<DraftDraw, Displacement>();
  const pending = [...drafts];

  let lastTier: Tier | null = null;
  let lastMode: ModeId | null = null;
  let secondLastMode: ModeId | null = null;
  const tierCompromises: SpacingCompromises = { capForced: 0, poolForced: 0 };
  const modeCompromises: SpacingCompromises = { capForced: 0, poolForced: 0 };

  const allowed = (draft: DraftDraw): boolean =>
    !ledger.wouldExceedCap(tierOf(draft.entry), { wasDueReview: draft.entry.wasDueReview });

  /**
   * `alternativeExisted` is evaluated over the whole pending list ignoring admissibility: if a
   * draw that would have satisfied the constraint was there and we still could not take it, the
   * cap is what refused it.
   */
  const note = (counter: SpacingCompromises, alternativeExisted: boolean): void => {
    if (alternativeExisted) counter.capForced += 1;
    else counter.poolForced += 1;
  };

  const tierOk = (draft: DraftDraw): boolean => tierOf(draft.entry) !== lastTier;
  const modeOk = (draft: DraftDraw): boolean =>
    !(lastMode !== null && lastMode === secondLastMode && draft.mode === lastMode);

  const emit = (draft: DraftDraw): void => {
    const at = options.now + draws.length;
    ledger.record({
      tier: tierOf(draft.entry),
      at,
      wasDueReview: draft.entry.wasDueReview,
    });
    draws.push({
      index: draws.length,
      item: draft.entry.item,
      tier: tierOf(draft.entry),
      mode: draft.mode,
      wasDueReview: draft.entry.wasDueReview,
      at,
    });
    lastTier = tierOf(draft.entry);
    secondLastMode = lastMode;
    lastMode = draft.mode;
  };

  /**
   * Record that the cap passed over this draw, once. Re-offering it later marks the same record
   * rather than adding a second one — one item held back is one displacement, however many
   * positions it waited through.
   */
  const noteDisplacement = (draft: DraftDraw, atIndex: number): void => {
    if (displacementOf.has(draft)) return;
    const record: Displacement = {
      itemId: draft.entry.item.id,
      tier: tierOf(draft.entry),
      mode: draft.mode,
      reason: 'grammar-cap',
      atIndex,
      reoffered: false,
    };
    displaced.push(record);
    displacementOf.set(draft, record);
  };

  while (pending.length > 0) {
    /**
     * Preference order, and the order matters:
     *   1. admissible, and keeps both spacing constraints;
     *   2. admissible, and keeps tier spacing;
     *   3. admissible at all.
     *
     * The cap necessarily reorders — it holds a grammar draw back and lets later ones through —
     * and taking the next admissible draft in strict order was quietly undoing the interleaving
     * the previous stage had just established. Found by the T28 simulation: 62 of 200 rounds
     * carried a tier-spacing violation the pool had an alternative for. See DECISIONS D-062.
     */
    let index = pending.findIndex((d) => allowed(d) && tierOk(d) && modeOk(d));
    if (index === -1) {
      index = pending.findIndex((d) => allowed(d) && tierOk(d));
      if (index !== -1) {
        // Mode spacing gave way. The cap is only to blame if a draw that would have kept it
        // existed and was refused; otherwise the pool simply had no other mode to offer.
        note(modeCompromises, pending.some((d) => tierOk(d) && modeOk(d)));
      }
    }
    if (index === -1) {
      index = pending.findIndex((d) => allowed(d));
      if (index !== -1) {
        note(tierCompromises, pending.some((d) => tierOk(d)));
        if (!modeOk(pending[index]!)) note(modeCompromises, pending.some((d) => modeOk(d)));
      }
    }

    if (index === -1) {
      // Nothing left is admissible, so everything remaining is discretionary grammar the window
      // cannot fit. It is deferred to the next run, not dropped.
      for (const draft of pending) noteDisplacement(draft, draws.length);
      break;
    }

    // Anything ahead of the chosen draft that the CAP refused (rather than spacing) has been
    // passed over, and that is what a displacement is.
    for (let i = 0; i < index; i++) {
      const skipped = pending[i]!;
      if (!allowed(skipped)) noteDisplacement(skipped, draws.length);
    }

    const [draft] = pending.splice(index, 1);
    emit(draft!);
    const record = displacementOf.get(draft!);
    if (record) record.reoffered = true;
  }

  return { draws, displaced, deferred: pending, ledger, tierCompromises, modeCompromises };
}

// ---------------------------------------------------------------------------------------------
// T27d — escalation
// ---------------------------------------------------------------------------------------------

/** Default ronda length: 90 seconds (BRIEF <game>). */
export const DEFAULT_RUN_MS = 90_000;

/**
 * Probability that a scaffold is offered, by third of the run.
 *
 * Decaying, not constant: the run should get harder as it goes (RESEARCH §3, Challenge). The first
 * third is generous because the 3:40 a.m. session may be the learner's only one that day and
 * starting cold with no support is how a session gets abandoned; the last third is nearly bare,
 * because that is where the retrieval effort that actually produces learning happens (RESEARCH §1).
 */
export const SCAFFOLD_PROBABILITY: readonly [number, number, number] = [0.55, 0.3, 0.08];

export interface RoundDraw extends Draw {
  /** Which third of the expected run this draw falls in, by estimated time rather than by count. */
  third: 0 | 1 | 2;
  /**
   * A scaffold is AVAILABLE on this card.
   *
   * Offering one is not using one. Only a reveal marks the attempt `hinted` and stops it advancing
   * the interval (invariant 2, SPEC §6.4); a card that offered a scaffold the learner ignored is a
   * completely clean attempt. Conflating the two would silently stop most early-run attempts from
   * scheduling anything at all.
   */
  scaffoldOffered: boolean;
}

export interface EscalationOptions {
  rng: Rng;
  runMs?: number;
  measured?: SecondsPerItem;
  scaffoldProbability?: readonly [number, number, number];
}

/**
 * Assign each draw its third and whether a scaffold is offered.
 *
 * Thirds are measured in **estimated seconds, not draw count**: a tier-5 free-typed sentence eats
 * more than twice the run a tier-2 cloze does, so counting cards would put the "final third" in
 * the wrong place on a sentence-heavy run.
 *
 * What they are measured *against* changed when the run timer was removed (D-263). It used to be
 * `runMs` — a fixed ninety seconds — so a queue deeper than the timer spent most of itself in the
 * final third, and a queue shallower than it never reached the final third at all. With no clock
 * the honest denominator is the plan's own estimated length: escalation is a shape across the run,
 * and the run is now exactly as long as it is.
 *
 * `runMs` is still accepted and still respected when given, because the tests that pin the
 * escalation curve describe a run of a known length and should keep describing one.
 */
export function applyEscalation(draws: readonly Draw[], options: EscalationOptions): RoundDraw[] {
  const probability = options.scaffoldProbability ?? SCAFFOLD_PROBABILITY;
  const measured = options.measured ?? {};

  // The whole plan, in estimated seconds. Zero for an empty plan, which `thirdOf` reads as "over".
  const totalMs =
    options.runMs ??
    draws.reduce((sum, draw) => sum + estimatedSeconds(draw.item, measured), 0) * 1000;

  const out: RoundDraw[] = [];
  let elapsedSeconds = 0;

  for (const draw of draws) {
    const third = thirdOf(elapsedSeconds * 1000, totalMs);
    out.push({
      ...draw,
      third,
      scaffoldOffered: options.rng.next() < (probability[third] ?? 0),
    });
    elapsedSeconds += estimatedSeconds(draw.item, measured);
  }

  return out;
}

function thirdOf(elapsedMs: number, totalMs: number): 0 | 1 | 2 {
  if (totalMs <= 0) return 2;
  const fraction = elapsedMs / totalMs;
  if (fraction < 1 / 3) return 0;
  if (fraction < 2 / 3) return 1;
  return 2;
}

/** Draws expected to fit in `runMs`. Used to place the late-run tier skew. */
export function expectedDrawCount(
  entries: readonly PoolEntry[],
  runMs = DEFAULT_RUN_MS,
  measured: SecondsPerItem = {},
): number {
  let seconds = 0;
  let count = 0;
  for (const entry of entries) {
    seconds += estimatedSeconds(entry.item, measured);
    count += 1;
    if (seconds * 1000 >= runMs) break;
  }
  return count;
}

// ---------------------------------------------------------------------------------------------
// The whole pipeline
// ---------------------------------------------------------------------------------------------

export interface RoundInput extends PoolInput {
  rng: Rng;
  /** The persisted seven-day draw window, so the cap does not start from an empty ledger. */
  history: readonly DrawRecord[];
  runMs?: number;
  quotaConfig?: QuotaConfig;
  lookahead?: number;
}

export interface RoundPlan {
  draws: RoundDraw[];
  pool: Pool;
  displaced: Displacement[];
  deferred: DraftDraw[];
  ledger: GrammarLedger;
  /** Spacing constraints the cap left no admissible alternative for. See `CapResult`. */
  tierCompromises: SpacingCompromises;
  modeCompromises: SpacingCompromises;
}

/**
 * Compose a round: pool → interleave → cap → escalation (SPEC §6.2).
 *
 * Two forked random streams, one for composition and one for scaffolds. Sharing a stream would
 * mean a scaffold roll changed which item is drawn next, and every recorded run would stop
 * replaying against a later build (DECISIONS D-008).
 */
export function buildRound(input: RoundInput): RoundPlan {
  const pool = buildPool(input);
  const runMs = input.runMs ?? DEFAULT_RUN_MS;
  const measured = input.measured ?? {};

  const drafts = interleave(pool.entries, {
    capabilities: input.capabilities,
    rng: input.rng.fork('draws'),
    ...(input.lookahead === undefined ? {} : { lookahead: input.lookahead }),
    escalation: { expectedDraws: expectedDrawCount(pool.entries, runMs, measured) },
  });

  const capped = applyGrammarCap(drafts, {
    now: input.now,
    history: input.history,
    runMs,
    ...(input.quotaConfig ? { quotaConfig: input.quotaConfig } : {}),
  });

  const draws = applyEscalation(capped.draws, {
    rng: input.rng.fork('scaffolds'),
    runMs,
    measured,
  });

  return {
    draws,
    pool,
    displaced: capped.displaced,
    deferred: capped.deferred,
    ledger: capped.ledger,
    tierCompromises: capped.tierCompromises,
    modeCompromises: capped.modeCompromises,
  };
}
